import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { CheckCircle2, Download, Printer } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

// TypeScript Interfaces
export interface FeeBreakdownItem {
  label: string;
  amount: number;
}

export interface PaymentReceiptProps {
  receiptId: string;
  paymentMethod: string;
  amountPaid: number;
  cashReceived?: number;
  change?: number;
  feeBreakdown: FeeBreakdownItem[];
  totalPaid: number;
  onContinue: () => void | Promise<void>;
  onDownloadReceipt?: () => void;
  onPrintReceipt?: () => void;
  loading?: boolean;
  continueButtonText?: string;
  className?: string;
  consumerDetailsData?:any
}

export const PaymentReceipt: React.FC<PaymentReceiptProps> = ({
  receiptId,
  paymentMethod,
  amountPaid,
  cashReceived,
  change,
  feeBreakdown,
  totalPaid,
  onContinue,
  onDownloadReceipt,
  onPrintReceipt,
  loading = false,
  continueButtonText = "Continue to Installation",
  className = "",
  consumerDetailsData
}) => {
    const currency = useCurrency();
      const { consumerId } = useParams<{ consumerId: string }>();
    const navigate=useNavigate()
     console.log("consumerDetailsData",JSON.stringify(consumerDetailsData) )
  const handleContinue = async () => {
  
    try {
      await onContinue();
    } catch (error) {
      console.error("Continue action error:", error);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm mt-4`}
    >
      {/* Success Header */}
      <div className="bg-blue-50 border-b border-blue-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-blue-900">
                Payment Completed
              </h2>
              <p className="text-sm text-blue-700 mt-0.5">
                Receipt #{receiptId}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
  <Button
  onClick={() => navigate(`/cx/payments/success`, {
    state: {
      // Pass the payment data from consumerDetailsData
      result: consumerDetailsData?.result?.paymentLastData,
      formData: {
        // Map the data to match what the success page expects
        customerInfo: {
          firstName: consumerDetailsData?.result?.firstName,
          lastName: consumerDetailsData?.result?.lastName,
          email: consumerDetailsData?.result?.email,
          contactNumber: consumerDetailsData?.result?.contactNumber,
          consumerNo: consumerDetailsData?.result?.consumerNo,
        },
        paymentMethod: consumerDetailsData?.result?.paymentLastData?.paymentModeDisplay,
        amount: consumerDetailsData?.result?.paymentLastData?.amount,
        // Add any other fields the success page needs
      }
    }
  })}
  className="flex items-center gap-2 justify-center border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
  variant="outline"
>
  <Printer className="w-4 h-4" />
  View Receipt
</Button>

     
          </div>
        </div>
      </div>

      {/* Receipt Content */}
      <div className="p-6 space-y-4">
        {/* Payment Method and Amount Paid Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payment Method */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Payment Method
            </p>
            <p className="text-base font-semibold text-gray-900">
              {paymentMethod}
            </p>
          </div>

          {/* Amount Paid */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Amount Paid
            </p>
            <p className="text-base font-semibold text-gray-900">
              {currency}  {amountPaid}
            </p>
          </div>
        </div>

        {/* Cash Received and Change Row (Only show if cash payment) */}
        {(cashReceived !== undefined || change !== undefined) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cash Received */}
            {cashReceived !== undefined && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs font-medium text-green-700 mb-1">
                  Cash Received
                </p>
                <p className="text-base font-semibold text-green-900">
                  {currency}  {cashReceived}
                </p>
              </div>
            )}

            {/* Change */}
            {change !== undefined && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs font-medium text-green-700 mb-1">
                  Change
                </p>
                <p className="text-base font-semibold text-green-900">
                  {currency}  {change.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Fee Breakdown */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Fee Breakdown
          </h3>

          <div className="space-y-2">
            {/* Fee Items */}
            {feeBreakdown.map((fee, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700">{fee.label}</span>
                <span className="text-gray-900 font-medium">
                  {currency}  {fee.amount.toFixed(2)}
                </span>
              </div>
            ))}

            {/* Total Paid */}
            <div className="pt-2 border-t border-gray-300 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Total Paid
              </span>
              <span className="text-base font-bold text-gray-900">
                {currency}  {totalPaid.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <div className="p-6 pt-0">
        <Button
          type="button"
          onClick={handleContinue}
     
          className="w-full"
        >
          {loading ? "Processing..." : continueButtonText}
        </Button>
      </div>
    </div>
  );
};

export default PaymentReceipt;